import { useEffect, useRef, useCallback } from 'react';

interface PollingOptions {
    enabled: boolean;
    interval: number;
    maxAttempts?: number;
    onMaxAttemptsReached?: () => void;
}

export const usePolling = (
    callback: () => Promise<boolean>,
    options: PollingOptions
) => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const attemptCountRef = useRef(0);
    const mountedRef = useRef(true);

    const startPolling = useCallback(() => {
        if (!options.enabled || intervalRef.current) return;

        attemptCountRef.current = 0;
        
        intervalRef.current = setInterval(async () => {
            if (!mountedRef.current) {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                return;
            }

            attemptCountRef.current++;

            if (options.maxAttempts && attemptCountRef.current > options.maxAttempts) {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                options.onMaxAttemptsReached?.();
                return;
            }

            try {
                const shouldStop = await callback();
                if (shouldStop && intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            } catch (error) {
                console.error('Polling error:', error);
            }
        }, options.interval);
    }, [callback, options]);

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        attemptCountRef.current = 0;
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        
        if (options.enabled) {
            startPolling();
        } else {
            stopPolling();
        }

        return () => {
            mountedRef.current = false;
            stopPolling();
        };
    }, [options.enabled, startPolling, stopPolling]);

    return { stopPolling, startPolling };
};